import { KeyboardEvent } from "react";
import { HtmlReactParser, stripTags } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType, EventPlatformEntry } from "@kaltura/mediaspace-shared-types";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { useButtonAnalytics, useEntryUrl } from "@kaltura/mediaspace-shared-hooks";
import { Description, EntryLink, Name, Presenters, Tags } from "./styled-components";
import { TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { EventListItemProps } from "./EventListItemProps";
import { Breakpoint } from "@mui/material";

export interface EventListItemContentProps
    extends Pick<
        EventListItemProps,
        "entryLink" | "disabledMode" | "forwardedRef" | "headingLevel" | "hidePresenterModal"
    > {
    className?: string;
    data: EventPlatformEntry;
    dense?: boolean;
    openInNewTab?: boolean;
    onPresentersShowMore?: () => void;
    onDescriptionShowMore?: () => void;
    /**
     * breakpoint for showing 3 presenters in a row (instead of 2)
     */
    morePresentersBreakpoint?: Breakpoint | number;
    /**
     * breakpoint for aligning the presenters vertically
     */
    verticalBreakpoint?: Breakpoint | number;
}

/**
 * The main content of the session card - title, tags, description, presenters.
 */
export const EventListItemContent = ({
    className,
    data,
    entryLink,
    openInNewTab,
    disabledMode,
    forwardedRef,
    headingLevel,
    hidePresenterModal = false,
    dense,
    morePresentersBreakpoint,
    verticalBreakpoint,
    onPresentersShowMore,
    onDescriptionShowMore,
}: EventListItemContentProps) => {
    const context = useConfig();

    const showPresenterModal = context.eventPlatform?.eventListItem?.showPresenterModal && !hidePresenterModal;

    const sendButtonAnalytics = useButtonAnalytics();

    // disable title click by tab in disabledMode
    const handleDisabledMode = (event: KeyboardEvent) => {
        if (disabledMode && event.key !== "Tab") {
            event.preventDefault();
        }
    };

    entryLink = useEntryUrl({ entry: data, entryUrl: entryLink });

    return (
        <div className={className}>
            <TruncateWithTooltip text={data.name} tooltipPlacement={"top"}>
                <Name component={headingLevel ?? "h3"} variant={"h5"}>
                    <EntryLink
                        className={"kms-ds-event-list-item-title"}
                        href={entryLink}
                        target={openInNewTab ? "_blank" : undefined}
                        disabled={disabledMode}
                        ref={forwardedRef}
                        onKeyDown={handleDisabledMode}
                        onClick={() => sendButtonAnalytics("Session card - Name", ButtonClickAnalyticsType.LINK)}
                    >
                        {data.name}
                    </EntryLink>
                </Name>
            </TruncateWithTooltip>
            <Tags className="kms-ds-event-list-item-tags" tags={data.tags} />
            <Description
                className="kms-ds-event-list-item-description"
                lines={2}
                showMore={onDescriptionShowMore !== undefined}
                onToggle={onDescriptionShowMore}
                preventStateUpdate={true}
            >
                {/* we use HtmlReactParser here to decode html entities */}
                {HtmlReactParser(stripTags(data.description ?? ""))}
            </Description>

            {data.presenters && (
                <Presenters
                    className="kms-ds-event-list-item-presenters"
                    presenters={data.presenters}
                    showPresenterModal={showPresenterModal}
                    onShowMore={onPresentersShowMore}
                    dense={dense}
                    moreItemsBreakpoint={morePresentersBreakpoint}
                    verticalBreakpoint={verticalBreakpoint}
                    showLargePresenters={!dense}
                />
            )}
        </div>
    );
};
